<template>
  <!-- Passo 1 de 2: escolher como o app se parece. Vem **antes** da apresentação porque é
       ela que o jogador vai ler em seguida — apresentar o jogo no tema errado e só depois
       perguntar seria mostrar duas vezes a mesma coisa. Fundo neutro, não a marca: aqui
       quem tem de aparecer são as três amostras. -->
  <section v-if="pickingTheme" class="screen pad theme-first">
    <ThemePicker :step="welcomeSeen ? '' : 'passo 1 de 2'" @done="finishThemeStep" />
  </section>

  <!-- Primeira tela de quem chega. É a única do app inteiro com a marca no chão: aqui ela
       é o assunto, e da segunda tela em diante volta a ser só o brilho do topo
       (diretiva 01). -->
  <section v-else class="screen welcome">
    <img class="default" src="/marca/padronagem-escura.svg" alt="" aria-hidden="true" />

    <div class="core">
      <img class="symbol" src="/marca/simbolo-gradiente.svg" alt="" aria-hidden="true" />
      <img class="logo" src="/marca/logotipo-branco.svg" alt="Sentido" />
      <p class="pitch">{{ steps[step].text }}</p>

      <div class="points" role="tablist" aria-label="Passos da apresentação">
        <button
          v-for="(p, i) in steps"
          :key="p.text"
          type="button"
          role="tab"
          class="dot"
          :class="{ on: i === step }"
          :aria-selected="i === step"
          :aria-label="`Passo ${i + 1}`"
          @click="step = i"
        />
      </div>
    </div>

    <div class="footer">
      <button class="btn btn-graphite" type="button" @click="play">
        {{ step < steps.length - 1 ? 'Continuar' : 'Começar a jogar' }}
      </button>
      <button class="have-account" type="button" @click="signIn">Já tenho conta</button>
    </div>
  </section>
</template>

<script>
import ThemePicker from '../components/settings/ThemePicker.vue';
import { firstTime as neverPickedTheme } from '../lib/theme';

export const WELCOME_KEY = 'sentido:welcome';

/** A apresentação já foi vista neste aparelho? */
function readWelcomeSeen() {
  try {
    return window.localStorage.getItem(WELCOME_KEY) === '1';
  } catch {
    return false;
  }
}

export default {
  name: 'WelcomeView',
  components: { ThemePicker },
  data() {
    return {
      /* Só quem nunca escolheu vê a pergunta. Quem já tem tema e caiu aqui de novo — por
         armazenamento limpo pela metade — segue direto para a apresentação. */
      pickingTheme: neverPickedTheme(),
      welcomeSeen: readWelcomeSeen(),
      step: 0,
      steps: [
        {
          text: 'Uma palavra puxa a outra. Você recebe a primeira e vai adivinhando a chain. '
            + 'Cada erro revela uma letra e derruba o valor da palavra.',
        },
        {
          text: 'A chain do dia é a mesma para todo mundo e vale ponto no ranking. '
            + 'A trilha e o treino você joga quando quiser.',
        },
        {
          text: 'No duelo, a mesma chain vai para um amigo. Cada um joga quando puder — '
            + 'ganha quem puxar o fio mais longe.',
        },
      ],
    };
  },
  methods: {
    /** Ver as boas-vindas uma vez basta: a partir daqui a porta de entrada é Hoje. */
    markSeen() {
      try {
        window.localStorage.setItem(WELCOME_KEY, '1');
      } catch {
        // Navegador com armazenamento bloqueado ainda joga; ele só revê esta tela.
      }
    },
    /** A apresentação foi vista? Então a tela existe só pela pergunta do tema. */
    finishThemeStep() {
      this.pickingTheme = false;
      if (this.welcomeSeen) this.$router.push('/hoje');
    },
    play() {
      if (this.step < this.steps.length - 1) {
        this.step += 1;
        return;
      }
      this.markSeen();
      this.$router.push('/hoje');
    },
    signIn() {
      this.markSeen();
      // Com `mode=signin` a tela de conta abre em login. Sem ele, quem já tem conta caía
      // no formulário de cadastro — que é o botão de cima, não este.
      this.$router.push({ path: '/conta', query: { mode: 'signin' } });
    },
  },
};
</script>

<style scoped>
/* A escolha de tema usa o chão do tema escolhido, e por isso não herda o gradiente da
   marca que a apresentação tem. */
.theme-first { justify-content: center; background: var(--bg); }

/* A tela inteira é a marca: todo texto dela é branco nos três temas. */
.welcome {
  position: relative;
  overflow: hidden;
  background: var(--screen-gradient);
}

.default {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.2;
  pointer-events: none;
}

.core {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0 34px;
  text-align: center;
}

.symbol { width: 104px; height: 104px; filter: drop-shadow(var(--symbol-shadow)); }
.logo { width: 210px; margin: 26px 0 18px; }

.pitch {
  margin: 0;
  min-height: 5.5em;
  font: 600 16px/1.5 var(--f-body);
  color: var(--on-brand);
}

.points { display: flex; gap: 7px; margin-top: 30px; }

.dot {
  width: 6px;
  height: 6px;
  padding: 0;
  border: none;
  border-radius: var(--r-pill);
  background: var(--on-brand);
  transition: width 0.2s ease-out, background 0.2s ease-out;
}

.dot.on { width: 24px; background: var(--on-brand); }

.footer {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 26px calc(34px + env(safe-area-inset-bottom));
}

/* Sobre o gradiente, o CTA é grafite: um gradiente por tela, e ele já é o fundo. */
.footer .btn-graphite { min-height: 56px; font: 700 15px/1 var(--f-title); }

.have-account {
  padding: 8px;
  min-height: var(--touch);
  border: none;
  background: none;
  color: var(--on-brand);
  font: 700 13px/1 var(--f-body);
}
</style>
